import { Link } from '@inertiajs/react';
import type { ReactNode } from 'react';

import { trackUmamiEvent } from '@/lib/analytics';

// «Folha de Registo» — the unified admin detail layout shared by Mensagem,
// Pedido and Denúncia. One institutional sheet (a single surface), not stacked
// cards: a tricolour top edge, a timbrado masthead, hairline-ruled sections, a
// "livro de registo" footer (audit trail) and a bottom action bar.

/** Back link shown above the sheet (e.g. "Voltar a Pedidos"). */
export function RecordBackLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="text-on-surface-light hover:text-primary mb-4 inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
    >
      <span className="material-symbols-outlined text-[18px]">arrow_back</span>
      {label}
    </Link>
  );
}

/** Outer sheet: centred column, tricolour top edge, surface card. */
export function RecordSheet({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="border-line-strong bg-surface overflow-hidden rounded-md border shadow-[0_22px_50px_-34px_rgba(33,29,24,0.55)]">
        <div className="gov-rule" />
        {children}
      </div>
    </div>
  );
}

interface RecordMastheadProps {
  /** Small gold eyebrow, e.g. "Pedido de serviço". */
  typeTag: string;
  title: string;
  /** Right-hand slot of the eyebrow row — a StatusStamp or a reference code. */
  topRight?: ReactNode;
  /** Sender · email · date line under the title. */
  meta?: ReactNode;
  /** Optional workflow tracker (Denúncia). */
  tracker?: ReactNode;
}

export function RecordMasthead({ typeTag, title, topRight, meta, tracker }: RecordMastheadProps) {
  return (
    <div className="border-line border-b px-6 py-5">
      <div className="mb-2.5 flex items-start justify-between gap-3">
        <span className="text-secondary text-[10px] font-bold tracking-[0.14em] uppercase">
          {typeTag}
        </span>
        {topRight}
      </div>
      <h1 className="font-headline text-on-surface text-xl leading-tight font-semibold sm:text-2xl">
        {title}
      </h1>
      {meta && (
        <div className="text-on-surface-light mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
          {meta}
        </div>
      )}
      {tracker && <div className="mt-4">{tracker}</div>}
    </div>
  );
}

/** Separator between meta items: a faint mid-dot. */
export function MetaDot() {
  return (
    <span className="text-line-strong" aria-hidden="true">
      ·
    </span>
  );
}

/** Holds the ruled content sections (hairline dividers between them). */
export function RecordBody({ children }: { children: ReactNode }) {
  return <div className="divide-line/70 divide-y">{children}</div>;
}

/** One labelled section: an uppercase eyebrow + free content. */
export function RecordSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="px-6 py-4">
      <p className="text-on-surface-light/70 mb-2.5 text-[10px] font-bold tracking-[0.1em] uppercase">
        {label}
      </p>
      {children}
    </div>
  );
}

/** Free-text body (message, descrição, …) with preserved line breaks. */
export function RecordText({ children }: { children: ReactNode }) {
  return <p className="text-on-surface text-sm leading-relaxed whitespace-pre-wrap">{children}</p>;
}

export interface RecordDef {
  label: string;
  value: ReactNode;
  full?: boolean;
}

/** Two-column definition grid for structured field groups. */
export function RecordDefs({ items }: { items: RecordDef[] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-5 gap-y-3">
      {items.map((it) => (
        <div key={it.label} className={it.full ? 'col-span-2' : ''}>
          <dt className="text-on-surface-light/70 mb-0.5 text-[10px] tracking-wide uppercase">
            {it.label}
          </dt>
          <dd className="text-on-surface text-sm font-medium break-words">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface RecordFile {
  filename: string;
  url: string;
}

/** Attachment list — strips the storage UUID prefix from filenames. */
export function RecordFiles({ files }: { files: RecordFile[] }) {
  return (
    <ul className="space-y-1.5">
      {files.map((f) => (
        <li key={f.url}>
          <a
            href={f.url}
            target="_blank"
            rel="noopener noreferrer"
            download
            onClick={() => trackUmamiEvent('admin_file_download', { source: 'record_detail' })}
            className="text-secondary hover:text-secondary-dark inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
          >
            <span className="material-symbols-outlined text-[16px]">download</span>
            {f.filename.replace(/^[a-f0-9-]+-/, '')}
          </a>
        </li>
      ))}
    </ul>
  );
}

export interface LedgerItem {
  label: string;
  value: ReactNode;
}

/** "Livro de registo" footer — the audit trail strip on shaded paper. */
export function RecordLedger({ items }: { items: LedgerItem[] }) {
  const shown = items.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div className="border-line bg-background-deep flex flex-wrap gap-x-6 gap-y-1 border-t px-6 py-3">
      {shown.map((it) => (
        <span key={it.label} className="text-on-surface-light text-xs">
          {it.label} <span className="text-on-surface font-medium">{it.value}</span>
        </span>
      ))}
    </div>
  );
}

/** Bottom action bar inside the sheet. */
export function RecordActions({ children }: { children: ReactNode }) {
  return (
    <div className="border-line flex flex-wrap items-center gap-2 border-t px-6 py-3.5">
      {children}
    </div>
  );
}
